import {
  Dialog,
  DialogContent,
  DialogTitle,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import isLightColor from 'utils/isLightColor';

const CHECK_INTERVAL_MS = 3 * 60 * 1000; // har 3 daqiqada
// Oxirgi ko'rsatilgan vaqt localStorage'da — boshqa sahifalarga o'tib
// qaytganda ham 3 daqiqalik ritm saqlanadi (CRMga qaytganda muddati
// o'tgan bo'lsa darhol ochiladi).
const LAST_SHOWN_KEY = 'crm-taskless-last-shown';

/**
 * Topshiriqsiz lidlar haqida ogohlantirish modali.
 * CrmSettings.notification yoniq bo'lsa — har 3 daqiqada ochiladi (20 tadan, sahifalab).
 */
const TasklessLeadsModal = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);

  // Sozlama yoniqmi
  const { data: settings } = useQuery({
    queryKey: '/crm/settings/index',
    queryFn: async () =>
      (await axiosPrivate.get('/crm/settings/index')).data?.data,
    retry: false,
    staleTime: 60000,
  });

  const enabled = settings?.notification == 1;

  // Tekshirish ritmi: oxirgi ko'rsatilgandan beri 3 daqiqa o'tgan bo'lsa —
  // darhol (CRMga endi kirgan bo'lsa ham), aks holda qolgan vaqtdan keyin,
  // so'ng har 3 daqiqada.
  useEffect(() => {
    if (!enabled) return undefined;
    let intervalId;
    const last = parseInt(localStorage.getItem(LAST_SHOWN_KEY) || '0', 10);
    const elapsed = Date.now() - last;
    const initialDelay =
      elapsed >= CHECK_INTERVAL_MS ? 0 : CHECK_INTERVAL_MS - elapsed;
    const timeoutId = setTimeout(() => {
      setTick((p) => p + 1);
      intervalId = setInterval(() => setTick((p) => p + 1), CHECK_INTERVAL_MS);
    }, initialDelay);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [enabled]);

  const { data: leads, isFetching } = useQuery({
    queryKey: ['/crm/settings/taskless-leads', tick, page],
    queryFn: async () =>
      (await axiosPrivate.get(`/crm/settings/taskless-leads?page=${page}`))
        .data?.data,
    enabled: !!enabled,
    retry: false,
    keepPreviousData: true,
  });

  // Lid bor va muddati kelgan bo'lsa — modal ochilsin (ochilgan vaqt saqlanadi).
  // 10s tolerans — so'rov kechikishi tufayli 3-daqiqalik tick o'tkazib yubormasin.
  useEffect(() => {
    if (!enabled || !(leads?.total > 0)) return;
    const last = parseInt(localStorage.getItem(LAST_SHOWN_KEY) || '0', 10);
    if (Date.now() - last >= CHECK_INTERVAL_MS - 10000) {
      localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, leads?.total, enabled]);

  if (!enabled || !leads?.total) return null;

  const pageCount = Math.ceil((leads?.total || 0) / (leads?.per_page || 20));

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth='md'
      PaperProps={{ className: '!base-border !shadow-none min-w-[60vw]' }}
    >
      <DialogTitle className='!bg-[#FFFBEB] !text-warning-700'>
        <div className='flex items-center gap-2'>
          <i className='bi bi-exclamation-triangle' />
          {t('crm.tasklessModal.title')} — {leads?.total}
        </div>
      </DialogTitle>
      <DialogContent>
        {/* Shartnomalar jadvali (ContractTable) bilan bir xil ko'rinish */}
        <div className='component-table-wrapper base-table mt-4'>
          <TableContainer className='!shadow-none !base-border'>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    t('crm.tasklessModal.lead'),
                    t('lead.view.name'),
                    t('common.fields.staff'),
                    t('common.filter.status'),
                    t('crm.tasklessModal.lastAction'),
                  ].map((label, i) => (
                    <TableCell
                      key={`head-${i}`}
                      align='left'
                      sx={{ backgroundColor: 'rgb(var(--c-gray-50))', fontSize: '14px' }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className='overflow-hidden'>
                {(leads?.data || []).map((lead) => (
                  <TableRow
                    key={lead?.id}
                    hover
                    tabIndex={-1}
                    component={Link}
                    to={`/admin/crm/lead/edit/${lead?.id}`}
                    onClick={() => setOpen(false)}
                    className='no-underline cursor-pointer'
                  >
                    <TableCell>
                      <span className='font-medium text-ink whitespace-nowrap'>
                        {lead?.name}
                      </span>
                    </TableCell>
                    <TableCell>{lead?.contactone?.name || '—'}</TableCell>
                    <TableCell>{lead?.user?.name || '—'}</TableCell>
                    <TableCell>
                      {/* Shartnomalar jadvalidagi StatusBadge uslubi — yumshoq fon + rangli matn */}
                      <span
                        className='inline-flex items-center rounded-md text-[12px] leading-none font-medium px-2.5 py-1 whitespace-nowrap ring-1 ring-inset ring-black/5'
                        style={{
                          backgroundColor: `${
                            lead?.crm_block_item?.color || '#9CA3AF'
                          }1F`,
                          color: isLightColor(lead?.crm_block_item?.color)
                            ? '#374151'
                            : lead?.crm_block_item?.color || '#6B7280',
                        }}
                      >
                        {lead?.crm_block_item?.name || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {lead?.last_action
                        ? moment(lead?.last_action).format('DD.MM.YYYY HH:mm')
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {leads?.total > 0 && (
            <div className='relative p-3 mb-2 mt-4 flex items-center justify-center rounded-lg !shadow-none border border-[#E5E9EB] bg-[#FFFFFF]'>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, v) => setPage(v)}
                shape='rounded'
                color='primary'
                showFirstButton
                showLastButton
                disabled={isFetching}
              />
              <div className='absolute top-1/2 -translate-y-1/2 right-10'>
                {t('common.table.total')}: {leads?.total}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default memo(TasklessLeadsModal);
